import axios, { AxiosResponse } from 'axios';
import pickBy from 'lodash-es/pickBy';
import * as moment from 'moment';
import { analyticsApi, armorAPI } from '~/services/api';
import { AnalyticsApiTimeRangeFilter } from '~/utils/analytics';

interface SpendOverTime {
  labelCount: number;
  labelSpendUs: string;
  monthNum: number;
  quarter: number;
  totalSpendUs: string;
  transactionCount: number;
  weekNum: number;
  year: number;
  fullDate: string;
}

interface TotalSpendByCategory {
  billingTypeGroup: string;
  totalSpendUs: string;
}

interface LabelSpendByCarrier {
  carrier: string;
  labelSpendUs: string;
}

interface LabelSpendByService {
  carrier: string;
  serviceLevel: string;
  labelSpendUs: string;
}

interface LabelSpendByState {
  destState: string;
  stateName: string;
  labelSpendUs: string;
}

interface LabelSpendByCountry {
  destCountry: string;
  iso2: string;
  iso3: string;
  labelSpendUs: string;
}

const filterUndefinedParams = <T extends Record<string, unknown>>(params: T) =>
  pickBy<T>(params, (v) => v !== undefined);

export const ANALYTICS_API_URL_PATH_PREFIX = '/analytics/spend';

// Fetches us state metadata needed to display the US state heatmap
function getAllStatesMapConfig() {
  return axios.get('/geojson/allstates.json');
}

// TODO Remove this function once the fully native new dashboard rollout is complete
function getMetrics(startDate: Date, endDate: Date) {
  return armorAPI.request({
    url: `/reports/label`,
    method: 'GET',
    params: {
      from_date: moment.utc(startDate).format('YYYY-MM-DD'),
      to_date: moment.utc(endDate).format('YYYY-MM-DD'),
      period: 'day',
      measures: 'num_units',
      stats: 'sum',
    },
  });
}

function getSpendOverTime(
  rangeFilter: AnalyticsApiTimeRangeFilter,
): Promise<AxiosResponse<SpendOverTime[]>> {
  let params = {
    range_start_date: rangeFilter.rangeStartDate,
    range_end_date: rangeFilter.rangeEndDate,
  };
  const cleanedParams = filterUndefinedParams(params);
  return analyticsApi.request({
    url: `${ANALYTICS_API_URL_PATH_PREFIX}`,
    method: 'GET',
    params: cleanedParams,
  });
}

function getTotalSpendByCategory(
  rangeFilter: AnalyticsApiTimeRangeFilter,
): Promise<AxiosResponse<TotalSpendByCategory[]>> {
  let params = {
    range_start_date: rangeFilter.rangeStartDate,
    range_end_date: rangeFilter.rangeEndDate,
  };
  const cleanedParams = filterUndefinedParams(params);
  return analyticsApi.request({
    url: `${ANALYTICS_API_URL_PATH_PREFIX}/total/category`,
    method: 'GET',
    params: cleanedParams,
  });
}

function getLabelSpendByCarrier(
  rangeFilter: AnalyticsApiTimeRangeFilter,
): Promise<AxiosResponse<LabelSpendByCarrier[]>> {
  let params = {
    range_start_date: rangeFilter.rangeStartDate,
    range_end_date: rangeFilter.rangeEndDate,
  };
  const cleanedParams = filterUndefinedParams(params);
  return analyticsApi.request({
    url: `${ANALYTICS_API_URL_PATH_PREFIX}/label/carrier`,
    method: 'GET',
    params: cleanedParams,
  });
}

function getLabelSpendByService(
  rangeFilter: AnalyticsApiTimeRangeFilter,
): Promise<AxiosResponse<LabelSpendByService[]>> {
  let params = {
    range_start_date: rangeFilter.rangeStartDate,
    range_end_date: rangeFilter.rangeEndDate,
  };
  const cleanedParams = filterUndefinedParams(params);
  return analyticsApi.request({
    url: `${ANALYTICS_API_URL_PATH_PREFIX}/label/service`,
    method: 'GET',
    params: cleanedParams,
  });
}

function getLabelSpendByState(
  rangeFilter: AnalyticsApiTimeRangeFilter,
): Promise<AxiosResponse<LabelSpendByState[]>> {
  let params = {
    range_start_date: rangeFilter.rangeStartDate,
    range_end_date: rangeFilter.rangeEndDate,
  };
  const cleanedParams = filterUndefinedParams(params);
  return analyticsApi.request({
    url: `${ANALYTICS_API_URL_PATH_PREFIX}/label/state`,
    method: 'GET',
    params: cleanedParams,
  });
}

function getLabelSpendByCountry(
  rangeFilter: AnalyticsApiTimeRangeFilter,
): Promise<AxiosResponse<LabelSpendByCountry[]>> {
  let params = {
    range_start_date: rangeFilter.rangeStartDate,
    range_end_date: rangeFilter.rangeEndDate,
  };
  const cleanedParams = filterUndefinedParams(params);
  return analyticsApi.request({
    url: `${ANALYTICS_API_URL_PATH_PREFIX}/label/country`,
    method: 'GET',
    params: cleanedParams,
  });
}

export {
  SpendOverTime,
  TotalSpendByCategory,
  LabelSpendByCarrier,
  LabelSpendByService,
  LabelSpendByState,
  LabelSpendByCountry,
  getMetrics,
  getAllStatesMapConfig,
  getSpendOverTime,
  getTotalSpendByCategory,
  getLabelSpendByCarrier,
  getLabelSpendByService,
  getLabelSpendByState,
  getLabelSpendByCountry,
};
